import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Button,
  Group,
  MultiSelect,
  Select,
  Stack,
  Switch,
  TextInput,
  Textarea,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import {
  useDeleteMutation,
  useUpdateMutation,
} from "@supabase-cache-helpers/postgrest-react-query";
import { ErrorAlert, LoadingSuspense } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useCallback, useMemo } from "react";

import { api } from "@/api.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";
import { getPageRoutePrefixes } from "@/lib/routePrefixes.ts";

export const Route = createFileRoute("/admin/news/$id")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Edit news post`,
      },
    ],
  }),
});

const EditForm: FC<{ id: number }> = ({ id }) => {
  const { data } = useSuspenseQueryPostgrest(
    api.from("news_post").select("*").eq("id", id).single(),
  );
  const { t } = useLingui();

  const form = useForm({
    initialValues: {
      title: data.title,
      body: data.body,
      level: data.level,
      published: data.published,
      pinned: data.pinned,
      show_from: data.show_from ? new Date(data.show_from) : null,
      show_to: data.show_to ? new Date(data.show_to) : null,
      page_prefixes: data.page_prefixes ?? [],
    },
    validate: {
      title: (value) => (value.trim().length > 0 ? null : t`Title is required`),
      body: (value) => (value.trim().length > 0 ? null : t`Body is required`),
      show_to: (value, values) =>
        value && values.show_from && value < values.show_from
          ? t`Show to must be after show from`
          : null,
    },
  });

  const { mutateAsync: update } = useUpdateMutation(
    api.from("news_post"),
    ["id"],
    "id",
  );
  const { mutateAsync: remove } = useDeleteMutation(
    api.from("news_post"),
    ["id"],
    "id",
  );
  const pagePrefixOptions = useMemo(
    () =>
      getPageRoutePrefixes().map((path) => ({
        value: path,
        label: path === "/" ? t`/ (Front page)` : path,
      })),
    [t],
  );
  const navigate = Route.useNavigate();

  const handleSubmit = useCallback<Parameters<(typeof form)["onSubmit"]>[0]>(
    async (values) => {
      try {
        await update({
          id,
          title: values.title,
          body: values.body,
          level: values.level,
          published: values.published,
          pinned: values.pinned,
          show_from: values.show_from?.toISOString() ?? null,
          show_to: values.show_to?.toISOString() ?? null,
          page_prefixes:
            values.page_prefixes.length > 0 ? values.page_prefixes : null,
        });
      } catch (e) {
        if (e instanceof Error) {
          form.setFieldError("__root", e.message);
        }
      }
    },
    [form, update, id],
  );

  const handleDelete = useCallback(() => {
    modals.openConfirmModal({
      title: t`Delete news post`,
      children: <Trans>Are you sure you want to delete this news post?</Trans>,
      labels: { confirm: t`Delete`, cancel: t`Cancel` },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        await remove({ id });
        await navigate({ to: "/admin/news" });
      },
    });
  }, [remove, id, navigate, t]);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <h1>
          <Trans>Edit news post</Trans>
        </h1>
        <TextInput
          label={t`Title`}
          withAsterisk
          disabled={form.submitting}
          key={form.key("title")}
          {...form.getInputProps("title")}
        />
        <Textarea
          label={t`Body`}
          description={t`Markdown is supported`}
          withAsterisk
          autosize
          minRows={4}
          disabled={form.submitting}
          key={form.key("body")}
          {...form.getInputProps("body")}
        />
        <Select
          label={t({ message: "Level", context: "news level" })}
          withAsterisk
          allowDeselect={false}
          disabled={form.submitting}
          data={[
            {
              value: "info",
              label: t({ message: "Info", context: "news level" }),
            },
            {
              value: "warning",
              label: t({ message: "Warning", context: "news level" }),
            },
          ]}
          key={form.key("level")}
          {...form.getInputProps("level")}
        />
        <Group grow>
          <DateTimePicker
            label={t`Show from`}
            description={t`Leave empty to show immediately`}
            clearable
            disabled={form.submitting}
            key={form.key("show_from")}
            {...form.getInputProps("show_from")}
          />
          <DateTimePicker
            label={t`Show to`}
            description={t`Leave empty to never expire`}
            clearable
            disabled={form.submitting}
            key={form.key("show_to")}
            {...form.getInputProps("show_to")}
          />
        </Group>
        <MultiSelect
          label={t`Shown on pages`}
          description={t`Leave empty to show on all pages`}
          placeholder={t`All pages`}
          searchable
          disabled={form.submitting}
          data={pagePrefixOptions}
          key={form.key("page_prefixes")}
          {...form.getInputProps("page_prefixes")}
        />
        <Switch
          label={t({ message: "Published", context: "news status" })}
          description={t`Unpublished posts are only visible to admins`}
          disabled={form.submitting}
          key={form.key("published")}
          {...form.getInputProps("published", { type: "checkbox" })}
        />
        <Switch
          label={t`Pinned`}
          description={t`Pinned posts cannot be dismissed by users`}
          disabled={form.submitting}
          key={form.key("pinned")}
          {...form.getInputProps("pinned", { type: "checkbox" })}
        />
        <Group justify="space-between" mt="md">
          <Button
            type="button"
            color="red"
            variant="outline"
            onClick={handleDelete}
            disabled={form.submitting}
          >
            <Trans>Delete</Trans>
          </Button>
          <Button
            type="submit"
            loading={form.submitting}
            disabled={form.submitting}
          >
            <Trans>Save</Trans>
          </Button>
        </Group>
        {form.errors.__root && <ErrorAlert>{form.errors.__root}</ErrorAlert>}
      </Stack>
    </form>
  );
};

function RouteComponent() {
  const { id } = Route.useParams();

  return (
    <LoadingSuspense>
      <EditForm id={parseInt(id)} />
    </LoadingSuspense>
  );
}
